import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getMediaJobs, getPublicQueueStatus } from '@/api/astraea';
import type { MemberSafeMediaJob } from '@/lib/member/safeAssets';
import type { PublicQueueStatusRow } from '@/lib/member/safeAssets';
import { PUBLIC_QUEUE_STATUS_LABELS } from '@/lib/member/queueStatus';

const JOB_TYPE_LABEL: Record<string, string> = {
  text_to_image: '文生图',
  image_to_image: '图生图',
  text_to_video: '文生影片',
  image_to_video: '图生影片',
  talking_avatar: '口播影片',
  voice_generation: '配音',
  material_photo: '素材照片',
  material_video: '素材影片',
  content: '内容生成',
  strategy: '策略生成',
  video: '影片生成',
};

function jobDisplayName(job: MemberSafeMediaJob): string {
  return JOB_TYPE_LABEL[job.display_type] || job.display_type || '生成任务';
}

export default function QueuePage() {
  const [queue, setQueue] = useState<PublicQueueStatusRow | null>(null);
  const [jobs, setJobs] = useState<MemberSafeMediaJob[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [q, j] = await Promise.all([
        getPublicQueueStatus(),
        getMediaJobs(40),
      ]);
      setQueue(q);
      setJobs(j);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const active = jobs.filter((j) => j.display_status === 'processing' || j.display_status === 'pending');

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/" className="hover:text-foreground-600">工作台</Link>
        <i className="ri-arrow-right-s-line" />
        <span>生成排队</span>
      </div>
      <h1 className="text-xl font-bold text-foreground-950">我的生成任务</h1>
      <p className="text-sm text-secondary-500 mt-1 mb-6">仅显示任务名称与状态，不含供应商或内部错误详情。</p>

      {loading ? (
        <p className="text-sm text-secondary-400">加载中…</p>
      ) : (
        <>
          {queue && (
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4 mb-6">
              <div className="text-sm font-semibold text-foreground-900">
                目前状态：{queue.label || PUBLIC_QUEUE_STATUS_LABELS[queue.status]}
              </div>
              <p className="text-xs text-secondary-600 mt-1">{queue.description}</p>
            </div>
          )}

          <p className="text-xs text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2 mb-4">
            <i className="ri-information-line mr-1" />
            可以离开此页，任务完成后会自动出现在「生成中心」或对应角色任务板。
          </p>

          {active.length === 0 ? (
            <p className="text-sm text-secondary-500">目前没有进行中的任务。</p>
          ) : (
            <ul className="space-y-3">
              {active.map((j) => (
                <li key={j.job_row_id} className="rounded-xl border border-secondary-200 bg-background-50 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground-900">{jobDisplayName(j)}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{j.status_label}</span>
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">{j.status_description}</p>
                  {j.estimated_wait_hint && (
                    <p className="text-xs text-secondary-400 mt-1">预估：{j.estimated_wait_hint}</p>
                  )}
                  {j.leave_hint && (
                    <p className="text-[11px] text-primary-600 mt-2">{j.leave_hint}</p>
                  )}
                  {j.display_status === 'completed' && j.output_url && (
                    <p className="text-[11px] text-secondary-500 mt-2">完成后可在「生成中心」查看与下载。</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {jobs.filter((j) => j.display_status === 'completed' || j.display_status === 'failed').length > 0 && (
            <section className="mt-8">
              <h2 className="text-sm font-semibold text-foreground-800 mb-3">近期纪录</h2>
              <ul className="space-y-2">
                {jobs
                  .filter((j) => j.display_status === 'completed' || j.display_status === 'failed')
                  .slice(0, 10)
                  .map((j) => (
                    <li key={`done-${j.job_row_id}`} className="text-xs text-secondary-600 flex justify-between gap-2 py-2 border-b border-secondary-100">
                      <span>{jobDisplayName(j)}</span>
                      <span>{j.status_label}</span>
                    </li>
                  ))}
              </ul>
              <Link to="/studio" className="inline-block mt-3 text-xs text-primary-600">前往生成中心查看 →</Link>
            </section>
          )}
        </>
      )}
    </div>
  );
}
